
import { useEffect, useRef, useState } from "react";
import {
  Mic, MicOff, Coffee,
  MessageCircle, Smile, Users, Sliders, Settings,
  Radio, LogOut,
} from "lucide-react";

/**
 * Phase 3.6 — Zoom-style bottom dock.
 *
 * Three equal sections separated by vertical dividers:
 *   Left   — local controls (Devices, BRB, Mic if DJ/host/cohost)
 *   Center — communication (chat, reactions, participants, DJ Board if DJ)
 *   Right  — DJ Share Audio toggle (if DJ), Manage dropdown (if host/cohost), Leave/End
 *
 * Buttons are 44×44 hit area with 20px icons + 11px label below — Zoom rhythm.
 *
 * Standalone CAM and Audio buttons removed — Devices gear handles device selection.
 * MIC only shown to DJ, host, or cohost (listeners don't broadcast audio).
 */
const QUICK_REACTS = ["👋", "👏", "🔥", "🎉", "💯", "❤️"];

export default function BottomDock({
  brb, onToggleBrb,
  participantCount, onOpenParticipants,
  chatUnread, onOpenChat,
  isCurrentDj,
  djBoardOpen,
  onOpenDjBoard,
  shareAudioOn, onToggleShareAudio,
  isStaff, manageSlot,
  // Phase A R4 B3 — contextual leave/end. Single button. Label flips based on isHost.
  isHost = false,
  onLeave,
  onEndRoom,
  onSelfReact,
  // R13-D — in-room Edit Devices flip-back
  onOpenDevices,
  // Mic control for DJ/host/cohost
  micOn = false,
  onToggleMic,
}) {
  const [reactOpen, setReactOpen] = useState(false);
  const reactRef = useRef(null);

  useEffect(() => {
    if (!reactOpen) return;
    const onClick = (e) => { if (reactRef.current && !reactRef.current.contains(e.target)) setReactOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setReactOpen(false); };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onEsc);
    return () => { window.removeEventListener("mousedown", onClick); window.removeEventListener("keydown", onEsc); };
  }, [reactOpen]);

  // Show mic only to DJ, host, or cohost
  const showMic = isCurrentDj || isStaff;

  return (
    <footer className="sticky bottom-0 z-30 flex items-stretch px-4 backdrop-blur-md bg-gradient-to-t from-[#0a0a0e]/95 to-[#0a0a0e]/65 border-t border-white/10"
            data-testid="bottom-dock"
            style={{ minHeight: 64, height: 64 }}>
      {/* Left section — local controls */}
      <div className="flex-1 flex items-center gap-2 justify-start min-w-0" data-testid="bottom-dock-left">
        {onOpenDevices && (
          <DockButton icon={Settings} label="Devices" testid="room-edit-devices" onClick={onOpenDevices} />
        )}
        <DockButton icon={Coffee} label="BRB" testid="bottom-dock-brb-btn" active={brb} accent="magenta" onClick={onToggleBrb} />
        {showMic && (
          <DockButton
            icon={micOn ? Mic : MicOff}
            label={micOn ? "Mic On" : "Mic Off"}
            testid="bottom-dock-mic"
            active={micOn}
            accent={micOn ? undefined : undefined}
            onClick={onToggleMic}
            title={micOn ? "Mute microphone" : "Unmute microphone"}
          />
        )}
      </div>

      <Divider />

      {/* Center section — communication */}
      <div className="flex-1 flex items-center gap-2 justify-center" data-testid="bottom-dock-center">
        <DockButton icon={MessageCircle} label="Chat" testid="bottom-dock-chat-btn" onClick={onOpenChat}
                    badge={chatUnread > 0 ? (chatUnread > 99 ? "99+" : String(chatUnread)) : null}
                    badgeColor="magenta" />
        <div className="relative" ref={reactRef}>
          <DockButton icon={Smile} label="React" testid="bottom-dock-react-btn" onClick={() => setReactOpen((v) => !v)} />
          {reactOpen && (
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-[#16161d] border border-white/15 rounded-xl p-2 shadow-2xl z-40"
                 data-testid="bottom-dock-react-picker">
              <div className="flex gap-1">
                {QUICK_REACTS.map((e) => (
                  <button key={e} onClick={() => { onSelfReact?.(e); setReactOpen(false); }}
                          className="w-9 h-9 grid place-items-center text-xl rounded hover:bg-white/10"
                          data-testid={`bottom-dock-react-${e}`}>{e}</button>
                ))}
              </div>
            </div>
          )}
        </div>
        <DockButton icon={Users} label="People" testid="bottom-dock-participants-btn" onClick={onOpenParticipants}
                    badge={participantCount > 0 ? String(participantCount) : null} />
        {isCurrentDj && (
          <DockButton icon={Sliders} label="DJ Board" testid="bottom-dock-dj-board" accent="magenta" onClick={onOpenDjBoard} />
        )}
      </div>

      <Divider />

      {/* Right section — DJ broadcast / host actions / leave */}
      <div className="flex-1 flex items-center gap-2 justify-end" data-testid="bottom-dock-right">
        {isCurrentDj && (
          <button onClick={onToggleShareAudio}
                  className={`h-12 px-4 rounded-lg border text-[11px] font-bold uppercase tracking-[0.1em] flex items-center gap-2 transition-colors ${
                    shareAudioOn
                      ? "bg-[#4ade80]/15 border-[#4ade80]/45 text-[#4ade80]"
                      : "bg-[#ef4444]/10 border-[#ef4444]/40 text-[#ef4444] hover:bg-[#ef4444]/15"
                  }`}
                  data-testid="bottom-dock-share-audio"
                  title={shareAudioOn ? "Stop sharing audio (unpublishes track to participants)" : "Start sharing audio (publishes track to participants)"}>
            <Radio className="w-4 h-4" />
            {shareAudioOn ? "● LIVE" : "● OFF"}
            <span className="hidden md:inline text-white/55 font-normal normal-case ml-1 tracking-normal">Share Audio</span>
          </button>
        )}
        {isStaff && manageSlot}
        {/* Phase A R4 B3 — Single contextual Leave/End button.
            data-role flips live when host role transfers (e.g. promote/demote). */}
        <button onClick={isHost ? onEndRoom : onLeave}
                className={`h-12 px-4 rounded-lg ${isHost ? "bg-[#ef4444] hover:bg-[#ef4444]/85 ring-2 ring-[#ef4444]/40" : "bg-[#ef4444]/85 hover:bg-[#ef4444]"} text-white text-[11px] font-bold uppercase tracking-[0.1em] flex items-center gap-1.5`}
                data-testid="room-leave-or-end"
                data-role={isHost ? "host" : "participant"}
                title={isHost ? "End the meeting for everyone" : "Leave the room"}>
          <LogOut className="w-3.5 h-3.5" />
          {isHost ? "End Meeting" : "Leave"}
        </button>
      </div>
    </footer>
  );
}

function Divider() {
  return <span aria-hidden className="self-center w-px h-8 bg-white/10 mx-1" />;
}

function DockButton({ icon: Icon, label, badge, badgeColor, accent, active, disabled, onClick, testid, title, dataAttrs, inlinePrev }) {
  let color = "text-white/80 hover:text-white";
  if (disabled) color = "text-white/25 cursor-not-allowed";
  else if (active) color = (accent === "magenta") ? "text-[#FF007F]" : "text-[#00E0FF]";
  else if (accent === "magenta") color = "text-[#FF007F] hover:text-[#FF007F]";
  const bg = active ? "bg-white/10" : "hover:bg-white/5";
  const wrapperClass = inlinePrev ? "-ml-1" : "";
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled}
            className={`relative w-12 h-12 rounded-lg flex flex-col items-center justify-center gap-0.5 ${bg} ${color} transition-colors ${wrapperClass}`}
            data-testid={testid}
            title={title || label}
            {...(dataAttrs || {})}>
      <Icon className="w-5 h-5" strokeWidth={1.75} />
      <span className="text-[9px] uppercase tracking-[0.05em] font-semibold leading-none">{label}</span>
      {badge && (
        <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full text-[9px] font-bold grid place-items-center px-1 ${
          badgeColor === "magenta" ? "bg-[#FF007F] text-white"
          : badgeColor === "ok" ? "bg-[#4ade80] text-black"
          : "bg-white/15 text-white/85"
        }`}>{badge}</span>
      )}
    </button>
  );
}
